import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"
import dayjs from "dayjs"
import styled from "styled-components"

import { useLazyGetAlertsQuery } from "store/api/alertsApi"
import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetDevicesQuery } from "store/api/devicesApi"
import { ApiTypes, UITypes } from "types"
import { MonitoringReportAnnotations } from "types/MonitoringReportAnnotations"
import { ReactComponent as IconEye } from "assets/svg/icon-eye.svg"

import { convertAlertsToTableRows } from "utils/convertData"
import { isMobileDevice } from "utils/deviceDetection"
import { parseDateStringToUTCString } from "utils/parseDateStringToUTCString"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AlertsAcknowledgeModal } from "modals/AlertsAcknowledgeModal"
import { AlertsFreezeModal } from "modals/AlertsFreezeModal"
import { AlertsUnfreezeModal } from "modals/AlertsUnfreezeModal"
import { StatsReportModal } from "modals/StatsReportModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import ExportButton from "components/common/ExportButton"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import TableWrapper from "components/table/TableWrapper"

import { alertsTableHeaderCells } from "./AlertsTableHeaderCells"

import styles from "./index.module.scss"

const FreezeAcknowledgeButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-right: auto;
  row-gap: 3px;
`

interface AlertsPageContentProps {
  alertsList: ApiTypes.Model.Alert[]
  isFetching: boolean
  meta: ApiTypes.Model.Pagination | undefined
  onRefreshAlerts: () => void
}

export const AlertsPageContent = ({ alertsList, isFetching, meta, onRefreshAlerts }: AlertsPageContentProps) => {
  const [monitoringReportAnnotations, setMonitoringReportAnnotations] = useState<MonitoringReportAnnotations | null>(
    null,
  )
  const [selectedAlerts, setSelectedAlerts] = useState<ApiTypes.Model.Alert[]>([])
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false)
  const [showFreezeModal, setShowFreezeModal] = useState(false)
  const [showUnfreezeModal, setShowUnfreezeModal] = useState(false)
  const [triggerGetDevice] = useLazyGetDevicesQuery()
  const [selectedDevice, setSelectedDevice] = useState<ApiTypes.Model.Device | null>(null)
  const [isHeaderChecked, setIsHeaderChecked] = useState(false)
  const { urlQueryParams } = useUrlQueryParams()

  useEffect(() => {
    triggerGetTableColumns({ list_name: "contractor_alerts_list" })
  }, [])

  useEffect(() => {
    setIsHeaderChecked(allAlertsSelected())
  }, [alertsList, selectedAlerts])

  const allAlertsSelected = () => {
    if (alertsList.length === 0) return false
    return alertsList.every(item => selectedAlerts.findIndex(alert => alert.id === item.id) > -1)
  }

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(alertsTableHeaderCells, columnsData?.data?.config)
        : alertsTableHeaderCells,
    [columnsData],
  )

  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta,
    fileName: "contractor_alerts",
    convertData: convertAlertsToTableRows,
    useLazyQuery: useLazyGetAlertsQuery,
  })

  const onSelectAlert = (id: number) => {
    if (selectedAlerts.some(item => item.id === id)) {
      setSelectedAlerts(selectedAlerts.filter(item => item.id !== id))
    } else {
      setSelectedAlerts([...selectedAlerts, alertsList.find(item => item.id === id)!])
    }
  }

  const checkSelectedAlertsDiffer = () => {
    if (selectedAlerts.length === 0) return false
    const firstStatus = selectedAlerts[0].status
    const statusDiffer = selectedAlerts.some(alert => alert.status !== firstStatus)

    if (statusDiffer) {
      toast.error("Action cannot be applied. Please select alerts with the same status.")
      return true
    }

    return false
  }

  const onAcknowledgeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowAcknowledgeModal(true)
  }

  const onFreezeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowFreezeModal(true)
  }

  const onUnfreezeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowUnfreezeModal(true)
  }

  const handleSuccess = () => {
    onRefreshAlerts()
    setSelectedAlerts([])
    setShowAcknowledgeModal(false)
    setShowFreezeModal(false)
    setShowUnfreezeModal(false)
  }

  const toggleAllAlerts = () => {
    if (isHeaderChecked) {
      setSelectedAlerts([])
      setIsHeaderChecked(false)
    } else {
      setSelectedAlerts(alertsList)
      setIsHeaderChecked(true)
    }
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => {
    if (rowData.type?.value !== "Efficiency Issue") {
      return {
        className: "",
        actionButtons: [],
      }
    }

    return {
      className: styles.messageTemplateButtonContainer,
      actionButtons: [
        {
          icon: <IconEye />,
          tooltip: "View on chart",
          tooltipPosition: "top-start" as const,
          hidden: rowData.rowSettings.isEnable === false,
          handler: () => handleViewDetails(rowData),
        },
      ],
    }
  }

  const handleViewDetails = async (rowData: UITypes.TableBodyRow) => {
    const sn = rowData.sn.value as string | null
    const datetime = rowData.datetime.value as string | null

    if (!sn || !datetime) {
      return
    }

    const endDate = dayjs(parseDateStringToUTCString(datetime))
    const startDate = endDate.subtract(4, "hours")

    const res = await triggerGetDevice({ sn })

    if (res.error) {
      toast.error("Something went wrong, please try again")
    }

    if (res.data?.data[0]) {
      setSelectedDevice(res.data?.data[0])
      setMonitoringReportAnnotations({
        sn,
        start: startDate.utc().format("YYYY-MM-DD"),
        stop: endDate.utc().format("YYYY-MM-DD"),
        items: [
          {
            start: startDate.utc().format("YYYY-MM-DD HH:mm:ss"),
            stop: endDate.utc().format("YYYY-MM-DD HH:mm:ss"),
            tip: "System efficiency alert",
            color: "rgba(239, 68, 68, 0.5)",
          },
        ],
      })
    }
  }

  return (
    <ContentWrapper title="Alerts History">
      <nav className={styles.tableNavWrapper}>
        <FreezeAcknowledgeButtonsContainer>
          <span
            className={classNames(styles.statusButton, styles.acknowledge, {
              [styles.disabledStatusButton]: selectedAlerts.length === 0,
            })}
            onClick={onAcknowledgeClick}
          >
            Acknowledge
          </span>
          <span
            className={classNames(styles.statusButton, styles.active, {
              [styles.disabledStatusButton]: selectedAlerts.length === 0,
              [styles.frozen]: selectedAlerts.some(item => item.status === "frozen"),
              [styles.active]: selectedAlerts.some(item => item.status === "active"),
            })}
            onClick={selectedAlerts.some(item => item.status === "frozen") ? onUnfreezeClick : onFreezeClick}
          >
            {selectedAlerts.some(item => item.status === "frozen") ? "Unfreeze" : "Freeze"}
          </span>
        </FreezeAcknowledgeButtonsContainer>
        <div className={styles.tableNavButtonsWrapper}>
          <EditColumnsButton tableName="contractor_alerts_list" tableHeaderCells={tableHeaderCells} />
          <ExportButton onClick={handleExport} disabled={meta?.total === 0} />
        </div>
      </nav>
      <TableWrapper
        headerCells={tableHeaderCells}
        columnsLoaded={!!columnsData}
        data={convertAlertsToTableRows(alertsList)}
        isLoading={isFetching}
        onCheckboxChange={onSelectAlert}
        currentItemIDs={selectedAlerts.map(item => item.id)}
        getRowActions={getRowActions}
        onHeaderCheckboxChange={toggleAllAlerts}
        isHeaderChecked={isHeaderChecked}
        isMobileDevice={isMobileDevice()}
      />
      <Pagination meta={meta} />
      {showAcknowledgeModal && (
        <AlertsAcknowledgeModal
          onCancel={() => setShowAcknowledgeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
      {showFreezeModal && (
        <AlertsFreezeModal
          onCancel={() => setShowFreezeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
      {showUnfreezeModal && (
        <AlertsUnfreezeModal
          onCancel={() => setShowUnfreezeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
      {selectedDevice && (
        <StatsReportModal
          onCancel={() => setSelectedDevice(null)}
          device={selectedDevice}
          monitoringReportAnnotations={monitoringReportAnnotations}
          onClearMonitoringReportAnnotations={() => setMonitoringReportAnnotations(null)}
        />
      )}
    </ContentWrapper>
  )
}
